import * as React from 'react';
import { createStyles } from '@mantine/core';
import { MonthPickerInput as MonthPickerInputBase, MonthPickerInputProps } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import 'dayjs/locale/es-mx';
import 'dayjs/locale/en';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';
import { useCurrentParentLanguage } from '../../../hooks/useCurrentParentLanguage';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        error: commonFieldStyles.error,
        icon: commonFieldStyles.icon,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        calendarHeader: {
            display: 'none'
        }
    };
});

export interface CustomMonthPickerInputProps extends MonthPickerInputProps, GenericFieldProps {
    key?: string;
    showCalendarHeader?: boolean;
    noMinDate?: boolean;
}

export const MonthPickerInput: React.FC<CustomMonthPickerInputProps> = ({
    readOnly,
    labelKey,
    label,
    descriptionKey,
    tooltipKey,
    parentform,
    targetperiod,
    highlight,
    valueFormat,
    showCalendarHeader,
    noMinDate,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const { code: currentLanguage } = useCurrentParentLanguage();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });

    return (
        <MonthPickerInputBase
            classNames={{
                root: classes.root,
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly,
                    [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                error: classes.error,
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                }),
                calendarHeader: !showCalendarHeader ? classes.calendarHeader : undefined
            }}
            valueFormat={valueFormat ?? 'MMMM'}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            locale={currentLanguage === 'es' ? 'es-mx' : 'en'}
            description={descriptionLabel}
            minDate={noMinDate ? undefined : new Date(new Date().getFullYear(), 0, 1)}
            // maxDate={new Date(new Date().getFullYear(), 11, 1)}
            icon={<IconCalendar size="1.2rem" stroke={1.5} />}
            popoverProps={{
                withinPortal: true
            }}
            {...props}
        />
    );
};
